import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Compass, X } from 'lucide-react';
import { Button } from '@/components';
import { useUI } from '@/data/stores';

const TOUR_STEPS = [
  {
    path: '/',
    label: 'Command picture',
    title: 'Start with the national operating picture',
    body:
      'This is the executive and analyst landing view: current posture, live incident load, sector health, and the most important operational signals in one place.',
  },
  {
    path: '/map',
    label: 'Geospatial COP',
    title: 'Use the map to understand where risk is moving',
    body:
      'The map combines assets, dependencies, vessels, threats, and focus-mode filters. It is the fastest way to brief geography, cross-border exposure, and cascade paths.',
  },
  {
    path: '/assets',
    label: 'Assets',
    title: 'Inspect critical assets and digital twins',
    body:
      'The asset registry is the source of truth for infrastructure: owner, sector, status, criticality, telemetry, regulatory scope, and linked incidents.',
  },
  {
    path: '/alerts',
    label: 'Alerts',
    title: 'Triage alerts as operational incidents',
    body:
      'Alerts bring together cyber, physical, process, supply-chain, and geopolitical events with evidence, recommended actions, MITRE mappings, and affected assets.',
  },
  {
    path: '/scenarios',
    label: 'Scenarios',
    title: 'Model cascading consequences before they happen',
    body:
      'Scenarios simulate outages and attacks across dependency edges, showing impact propagation, affected services, and where intervention buys down risk.',
  },
  {
    path: '/ot',
    label: 'OT monitoring',
    title: 'Watch the control environment',
    body:
      'OT monitoring shows PLCs, RTUs, gateways, zones, protocols, baselines, vulnerabilities, and active enforcement posture for industrial networks.',
  },
  {
    path: '/intel',
    label: 'Intelligence',
    title: 'Connect activity to actors and campaigns',
    body:
      'The intelligence graph links reports, threat actors, campaigns, assets, and vulnerabilities so analysts can explain why an event matters.',
  },
  {
    path: '/compliance',
    label: 'Compliance',
    title: 'Translate operations into compliance evidence',
    body:
      'Compliance views map the live estate to NIS2, IEC 62443, ISO 27001, and other frameworks, with gaps and evidence status surfaced for auditors.',
  },
  {
    path: '/globe',
    label: '3D globe',
    title: 'Use the globe for briefings and maritime context',
    body:
      'The globe gives a presentation-grade view of regional infrastructure, subsea exposure, vessel context, and cross-border corridors.',
  },
  {
    path: '/settings',
    label: 'Settings',
    title: 'Tune the platform without changing the data',
    body:
      'Settings covers focus modes, roles, deployment posture, security controls, retention, and subscribed feeds. Focus modes reshape what operators see without deleting underlying records.',
  },
];

export function PlatformTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const open = useUI((s) => s.tourOpen);
  const step = useUI((s) => s.tourStep);
  const setStep = useUI((s) => s.setTourStep);
  const close = useUI((s) => s.closeTour);

  const clampedStep = Math.min(Math.max(step, 0), TOUR_STEPS.length - 1);
  const current = TOUR_STEPS[clampedStep];
  const progress = useMemo(
    () => `${clampedStep + 1} of ${TOUR_STEPS.length}`,
    [clampedStep],
  );

  useEffect(() => {
    if (!open) return;
    if (step !== clampedStep) {
      setStep(clampedStep);
      return;
    }
    if (location.pathname !== current.path) {
      navigate(current.path);
    }
  }, [clampedStep, current.path, location.pathname, navigate, open, setStep, step]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') setStep(Math.min(clampedStep + 1, TOUR_STEPS.length - 1));
      if (event.key === 'ArrowLeft') setStep(Math.max(clampedStep - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clampedStep, close, open, setStep]);

  if (!open) return null;

  const atStart = clampedStep === 0;
  const atEnd = clampedStep === TOUR_STEPS.length - 1;

  return (
    <div className="iris-tour" role="dialog" aria-modal="true" aria-labelledby="iris-tour-title">
      <button className="iris-tour-scrim" aria-label="Dismiss tour backdrop" onClick={close} />
      <section className="iris-tour-card">
        <header className="iris-tour-head">
          <div className="iris-tour-icon">
            <Compass size={18} strokeWidth={1.7} />
          </div>
          <div>
            <div className="iris-tour-eyebrow mono">{current.label}</div>
            <h2 id="iris-tour-title">{current.title}</h2>
          </div>
          <button className="iris-tour-close" aria-label="Close tour" onClick={close}>
            <X size={16} strokeWidth={1.7} />
          </button>
        </header>
        <p>{current.body}</p>
        <div className="iris-tour-dots" aria-label={progress}>
          {TOUR_STEPS.map((item, index) => (
            <button
              key={item.path}
              aria-label={`Go to ${item.label}`}
              className={index === clampedStep ? 'on' : ''}
              onClick={() => setStep(index)}
            />
          ))}
        </div>
        <footer className="iris-tour-actions">
          <span className="mono">{progress}</span>
          <div>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft size={14} strokeWidth={1.6} />}
              onClick={() => setStep(clampedStep - 1)}
              disabled={atStart}
            >
              Back
            </Button>
            <Button
              size="sm"
              variant={atEnd ? 'primary' : 'secondary'}
              iconRight={!atEnd ? <ArrowRight size={14} strokeWidth={1.6} /> : undefined}
              onClick={() => (atEnd ? close() : setStep(clampedStep + 1))}
            >
              {atEnd ? 'Done' : 'Next'}
            </Button>
          </div>
        </footer>
      </section>
    </div>
  );
}
